import React from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';
import moment from 'moment';
import TimePickerPanel from './panel';
import Input from '../input';
import Overlay from '../overlay';
import nextLocale from '../locale/zh-cn';
import { formatDateValue } from './utils';

const { Popup } = Overlay;
const timePickerLocale = nextLocale.TimePicker;
const noop = () => {};

const getFormatValues = (values, format) => {
  if (!Array.isArray(values)) {
    return [null, null];
  }
  return [formatDateValue(values[0], format), formatDateValue(values[1], format)];
};

class RangePicker extends React.Component {
  static propTypes = {
    /**
     * 设置trigger的className
     */
    className: PropTypes.string,
    /**
     * 设置trigger的style
     */
    style: PropTypes.object,
    /**
     * 设置trigger的style
     */
    size: PropTypes.oneOf(['small', 'medium']),
    /**
     * 设置组件className前缀
     */
    prefix: PropTypes.string,
    /**
     * 语言包
     */
    locale: PropTypes.object,
    /**
     * 弹层是否默认可见，初始化时有效
     */
    defaultVisible: PropTypes.bool,
    /**
     * 弹层是否可见，受控
     */
    visible: PropTypes.bool,
    /**
     * 弹层显隐回调
     */
    onVisibleChange: PropTypes.func,
    /**
     * 默认值，moment或字符串数组，非受控
     */
    defaultValue: PropTypes.array,
    /**
     * moment或字符串数组，受控
     */
    value: PropTypes.array,
    /**
     * 整体值发生变化时触发的回调方法
     */
    onChange: PropTypes.func,
    /**
     * 时间格式
     */
    format: PropTypes.string,
    /**
     * 小时选择步长
     */
    hourStep: PropTypes.number,
    /**
     * 分钟选择步长
     */
    minuteStep: PropTypes.number,
    /**
     * 秒选择步长
     */
    secondStep: PropTypes.number,
    /**
     * 禁用的小时，数组
     * @return {Boolean} 是否禁用
     */
    disabledHours: PropTypes.arrayOf(PropTypes.func),
    /**
     * 禁用的分钟，数组
     * @return {Boolean} 是否禁用
     */
    disabledMinutes: PropTypes.arrayOf(PropTypes.func),
    /**
     * 禁用的秒，数组
     * @return {Boolean} 是否禁用
     */
    disabledSeconds: PropTypes.arrayOf(PropTypes.func),
    /**
     * 弹层容器
     * @param {Object} target 目标节点
     * @return {ReactNode} 容器节点
     */
    popupContainer: PropTypes.func,
    /**
     * 弹层对齐方式, 详情见Overlay 文档
     */
    popupAlign: PropTypes.string,
    /**
     * 弹层触发方式
     */
    popupTriggerType: PropTypes.oneOf(['click', 'hover']),
    /**
     * 弹层自定义样式
     */
    popupStyle: PropTypes.object,
    /**
     * 弹层自定义样式类
     */
    popupClassName: PropTypes.string,
    /**
     * 弹层属性
     */
    popupProps: PropTypes.object,
    /**
     * 是否跟随滚动
     */
    followTrigger: PropTypes.bool,
    /**
     * 是否禁用
     */
    disabled: PropTypes.bool,
  }

  static defaultProps = {
    prefix: 'next-',
    size: 'medium',
    onVisibleChange: noop,
    onChange: noop,
    format: 'HH:mm:ss',
    locale: timePickerLocale,
    hourStep: 1,
    minuteStep: 1,
    secondStep: 1,
    disabled: false,
    disabledHours: [() => false, () => false],
    disabledMinutes: [() => false, () => false],
    disabledSeconds: [() => false, () => false],
    popupAlign: 'tl bl',
    popupTriggerType: 'click',
  }

  constructor(props) {
    super(props);
    const val = props.value || props.defaultValue;
    const value = getFormatValues(val, props.format);
    this.inputAsString = val && typeof val[0] === 'string' && typeof val[1] === 'string';
    this.state = {
      startInputStr: '',
      endInputStr: '',
      startInputing: false,
      endInputing: false,
      visible: props.visible || props.defaultVisible,
      startValue: value[0] || '',
      endValue: value[1] || '',
      isHover: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.visible !== nextProps.visible) {
      this.setState({
        visible: nextProps.visible,
      });
    }
    if (this.props.value !== nextProps.value) {
      const value = getFormatValues(nextProps.value, nextProps.format);
      this.setState({
        startValue: value[0],
        endValue: value[1],
      });
    }
  }

  onMouseOver = () => {
    this.setState({
      isHover: true,
    });
  }

  onMouseOut = () => {
    this.setState({
      isHover: false,
    });
  }

  onVisibleChange = (visible, type) => {
    if (!('visible' in this.props)) {
      this.setState({
        visible,
      });
    }
    this.props.onVisibleChange(visible, type);
  };

  onSelectStartTime = value => {
    const { startValue, endValue } = this.state;
    if (!('value' in this.props)) {
      this.setState({
        startValue: value,
        startInputing: false,
      });
    }
    if (!startValue || (startValue && value.valueOf() !== startValue.valueOf())) {
      this.onValueChange([value, endValue]);
    }
  }

  onSelectEndTime = value => {
    const { startValue, endValue } = this.state;
    if (!('value' in this.props)) {
      this.setState({
        endValue: value,
        endInputing: false,
      });
    }
    if (!endValue || (endValue && value.valueOf() !== endValue.valueOf())) {
      this.onValueChange([startValue, value]);
    }
  }

  onValueChange = (v) => {
    const { format } = this.props;
    const ret = this.inputAsString ? [v[0].format(format), v[1].format(format)] : v;
    this.props.onChange(ret);
  }

  onClearValue = () => {
    this.setState({
      startValue: '',
      endValue: '',
      startInputStr: '',
      endInputStr: '',
    });
    const ret = this.inputAsString ? ['', ''] : [null, null];
    this.props.onChange(ret);
  }

  onTriggerChange = (v, e, eventType) => {
    if (!('value' in this.props)) {
      if (eventType === 'clear') {
        e.stopPropagation();
        this.onClearValue();
      }
    } else if (eventType === 'clear') {
      // 受控状态下用户点击 clear
      e.stopPropagation();
      const ret = this.inputAsString ? ['', ''] : [null, null];
      this.props.onChange(ret);
    }
  }

  onStartInputChange = (value) => {
    if (!('value' in this.props)) {
      this.setState({
        startInputing: true,
        startInputStr: value,
      });
    }
  }

  onEndInputChange = (value) => {
    if (!('value' in this.props)) {
      this.setState({
        endInputing: true,
        endInputStr: value,
      });
    }
  }

  onStartInputBlur = () => {
    const { startValue, startInputStr, endValue } = this.state;
    if (!startValue && startInputStr) {
      const { format } = this.props;
      const parsed = moment(startInputStr, format, true);
      if (parsed.isValid()) {
        this.setState({
          startValue: parsed,
          startInputStr: '',
        });
        this.onValueChange([parsed, endValue]);
      }
      this.setState({
        startInputing: false,
      });
    }
  }

  onEndInputBlur = () => {
    const { startValue, endInputStr, endValue } = this.state;
    if (!endValue && endInputStr) {
      const { format } = this.props;
      const parsed = moment(endInputStr, format, true);
      if (parsed.isValid()) {
        this.setState({
          endValue: parsed,
          endInputStr: '',
        });
        this.onValueChange([startValue, parsed]);
      }
      this.setState({
        endInputing: false,
      });
    }
  }

  renderTrigger = () => {
    const {
      prefix,
      size,
      locale,
      disabled,
      format,
    } = this.props;
    const { startValue, endValue, startInputStr, endInputStr, startInputing, endInputing } = this.state;
    const startInputValue = startInputing ? startInputStr : (startValue && startValue.format(format)) || '';
    const endInputValue = endInputing ? endInputStr : (endValue && endValue.format(format)) || '';
    return (
      <div tabIndex={disabled ? null : 0}>
        <Input
          size={size}
          readOnly
          disabled={disabled}
          placeholder={locale.startPlaceholder}
          onChange={this.onTrigger}
          value={startInputValue}
          hasBorder={false}
          className={`${prefix}time-range-picker-trigger-start-input`}
        />
        <span className={`${prefix}time-range-picker-trigger-separator`}>~</span>
        <Input
          size={size}
          readOnly
          disabled={disabled}
          placeholder={locale.endPlaceholder}
          value={endInputValue}
          hasBorder={false}
          hint="clock"
          hasClear
          onChange={this.onTriggerChange}
          className={`${prefix}time-range-picker-trigger-start-input`}
        />
      </div>
    );
  }

  renderTimePanel = () => {
    const {
      prefix,
      locale,
      disabled,
      disabledHours,
      disabledMinutes,
      disabledSeconds,
      format,
      hourStep,
      minuteStep,
      secondStep,
    } = this.props;
    const { startValue, endValue, startInputStr, endInputStr, startInputing, endInputing } = this.state;
    const startInputValue = startInputing ? startInputStr : (startValue && startValue.format(format)) || '';
    const endInputValue = endInputing ? endInputStr : (endValue && endValue.format(format)) || '';
    const showSecond = format.indexOf('s') > -1;

    const sharedTimePickerProps = {
      prefix,
      locale,
      disabled,
      showSecond,
      hourStep,
      minuteStep,
      secondStep,
    };
    return (
      <div className={`${prefix}time-range-picker-panel`}>
        <div className={`${prefix}time-range-picker-panel-header`}>
          <Input
            size="xs"
            onChange={this.onStartInputChange}
            onBlur={this.onStartInputBlur}
            onPressEnter={this.onStartInputBlur}
            disabled={disabled}
            value={startInputValue}
            placeholder={format}
            className={`${prefix}time-range-picker-panel-start-input`}
          />
          <span className={`${prefix}time-range-picker-trigger-separator`}>~</span>
          <Input
            size="xs"
            onChange={this.onEndInputChange}
            onBlur={this.onEndInputBlur}
            onPressEnter={this.onEndInputBlur}
            disabled={disabled}
            value={endInputValue}
            placeholder={format}
            className={`${prefix}time-range-picker-panel-end-input`}
          />
        </div>
        <TimePickerPanel
          {...sharedTimePickerProps}
          disabledHours={disabledHours[0]}
          disabledMinutes={disabledMinutes[0]}
          disabledSeconds={disabledSeconds[0]}
          className={`${prefix}time-range-picker-start-panel`}
          value={startValue}
          onSelect={this.onSelectStartTime}
        />
        <TimePickerPanel
          {...sharedTimePickerProps}
          disabledHours={disabledHours[1]}
          disabledMinutes={disabledMinutes[1]}
          disabledSeconds={disabledSeconds[1]}
          className={`${prefix}time-range-picker-end-panel`}
          value={endValue}
          onSelect={this.onSelectEndTime}
        />
      </div>
    );
  }

  render() {
    const { visible, isHover } = this.state;
    const {
      prefix,
      followTrigger,
      popupProps,
      popupContainer,
      popupAlign,
      popupTriggerType,
      popupStyle,
      popupClassName,
      disabled,
      style,
      className,
    } = this.props;
    return (
      <div
        className={cls(
          {
            [`${prefix}time-range-picker`]: true,
            [`${prefix}time-range-picker-active`]: visible && !disabled,
            [`${prefix}time-range-picker-hover`]: isHover && !disabled,
            [`${prefix}time-range-picker-disabled`]: disabled,
            className,
          }
        )}
        style={style}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
      >
        <Popup
          {...popupProps}
          offset={[0, 4]}
          closable={false}
          trigger={this.renderTrigger()}
          triggerType={popupTriggerType}
          followTrigger={followTrigger}
          container={popupContainer}
          disabled={disabled}
          visible={visible}
          onVisibleChange={this.onVisibleChange}
          align={popupAlign}
          style={popupStyle}
          className={popupClassName}
        >
          {
            this.renderTimePanel()
          }
        </Popup>
      </div>
    );
  }
}

export default RangePicker;
